import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useRef, useState } from 'react';

import { campaignsApi, creativesApi, queryKeys } from '../../lib/api/resources';
import { ApiRequestError } from '../../lib/api/client';
import { count, date, money, percent, shortId } from '../../lib/format';
import { useToast } from '../../app/toast';
import {
  BudgetBar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  MetricCard,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
} from '../../components/ui';

/**
 * Everything about one campaign, including what was refused.
 *
 * <p>This is the screen an operator opens when an advertiser questions their numbers, so rejected
 * events are shown here rather than hidden. A delivery figure nobody can explain is worse than a
 * lower one that reconciles.
 */
export function CampaignDetailPage() {
  const { id = '' } = useParams();

  const campaign = useQuery({
    queryKey: queryKeys.campaign(id),
    queryFn: () => campaignsApi.get(id),
    enabled: Boolean(id),
  });

  const metrics = useQuery({
    queryKey: queryKeys.campaignMetrics(id),
    queryFn: () => campaignsApi.metrics(id),
    enabled: Boolean(id),
  });

  if (campaign.isLoading) {
    return <Skeleton className="h-96" />;
  }

  if (campaign.isError) {
    return (
      <ErrorState
        message={(campaign.error as Error).message}
        onRetry={() => void campaign.refetch()}
      />
    );
  }

  const data = campaign.data!;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-headline-lg text-text-primary">{data.name}</h1>
            <StatusBadge status={data.status} />
          </div>
          <p className="font-mono text-label-md text-text-secondary" title={data.id}>
            {shortId(data.id)} · {data.advertiserName ?? 'Sin anunciante'} · {data.appId} ·{' '}
            {data.placement}
          </p>
        </div>
        <Link to={`/campaigns/${data.id}/edit`}>
          <Button>Editar</Button>
        </Link>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.isLoading ? (
          Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28" />)
        ) : metrics.isError ? null : (
          <>
            <MetricCard label="Impresiones" value={count(metrics.data!.impressions)} />
            <MetricCard label="Clics" value={count(metrics.data!.clicks)} />
            <MetricCard label="CTR" value={percent(metrics.data!.ctr)} />
            <MetricCard
              label="Consumido"
              value={money(metrics.data!.amountConsumed)}
              hint={`Restante ${money(metrics.data!.budgetRemaining)}`}
            />
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Configuración">
          <dl className="flex flex-col gap-3">
            <DetailRow label="Modelo" value={data.pricingModel} />
            <DetailRow label="Precio" value={money(data.priceAmount)} />
            <DetailRow label="Presupuesto" value={money(data.budgetTotal)} />
            <DetailRow label="Prioridad" value={String(data.priority)} />
            <DetailRow label="Peso" value={String(data.weight)} />
            <DetailRow label="Vuelo" value={`${date(data.startDate)} → ${date(data.endDate)}`} />
          </dl>

          <div className="mt-4">
            <p className="mb-1 font-mono text-label-md uppercase text-text-secondary">
              Consumo del presupuesto
            </p>
            <BudgetBar consumed={data.budgetConsumed} total={data.budgetTotal} />
            <p className="mt-1 font-mono text-label-md text-text-secondary">
              {money(data.budgetConsumed)} de {money(data.budgetTotal)}
            </p>
          </div>
        </Card>

        <CreativePanel
          campaignId={data.id}
          placement={data.placement}
          minWatchSeconds={data.rewardMinWatchSeconds}
        />
      </div>

      <Card title="Eventos rechazados">
        {metrics.isLoading ? (
          <Skeleton className="h-40" />
        ) : metrics.isError ? (
          <ErrorState message={(metrics.error as Error).message} />
        ) : (
          <RejectedEvents rejected={metrics.data!.rejectedEvents} />
        )}
      </Card>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-body-md text-text-secondary">{label}</dt>
      <dd className="font-mono text-body-md text-text-primary">{value}</dd>
    </div>
  );
}

/**
 * Rejected events, by reason.
 *
 * <p>Reasons with a count of zero are hidden here, unlike in the API: an operator reading this
 * screen wants what happened, and a list of twelve zeroes buries the one number that is not.
 */
function RejectedEvents({ rejected }: { rejected: Record<string, number> }) {
  const entries = Object.entries(rejected).filter(([, value]) => value > 0);

  if (entries.length === 0) {
    return (
      <EmptyState
        title="Ningún evento rechazado"
        description="Todos los eventos recibidos en el periodo pasaron las validaciones."
      />
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Motivo</Th>
          <Th numeric>Eventos</Th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([reason, value]) => (
          <tr key={reason}>
            <Td>
              <span className="font-mono text-body-md">{reason}</span>
              <p className="text-label-md text-text-secondary">{REASON_HINTS[reason] ?? ''}</p>
            </Td>
            <Td numeric>{count(value)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** What each rejection means, in the operator's terms rather than the code's. */
const REASON_HINTS: Record<string, string> = {
  MISSING_TOKEN: 'El evento llegó sin token.',
  UNKNOWN_TOKEN: 'Token no emitido por este servidor, o ya expirado y evicto.',
  EXPIRED_TOKEN: 'El anuncio se sirvió hace demasiado tiempo.',
  TOKEN_ALREADY_USED: 'Un mismo anuncio reportó el evento dos veces.',
  CAMPAIGN_MISMATCH: 'El token pertenecía a otra campaña.',
  DUPLICATE: 'Reintento del cliente. No se cobra dos veces.',
  CLICK_WITHOUT_IMPRESSION: 'Clic sin impresión previa.',
  CLICK_TOO_FAST: 'Clic demasiado rápido para ser humano.',
  VALIDATION_UNAVAILABLE: 'Redis no estaba disponible. Incidencia de infraestructura, no fraude.',
};

/**
 * Creative preview and upload.
 *
 * <p>Shown at the real placement dimensions, because a banner that looks fine scaled down is how a
 * stretched creative reaches an advertiser's customers.
 */
function CreativePanel({
  campaignId,
  placement,
  minWatchSeconds,
}: {
  campaignId: string;
  placement: string;
  minWatchSeconds?: number | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [clickUrl, setClickUrl] = useState('');

  // ADS-0044: a rewarded ad is one the user watches in exchange for something, so it takes a
  // video. Offering an image picker here produced campaigns the server now refuses.
  const isRewarded = placement === 'REWARDED';

  const creative = useQuery({
    queryKey: queryKeys.creative(campaignId),
    queryFn: () => creativesApi.get(campaignId),
    // A campaign without a creative is normal, not an error worth retrying.
    retry: false,
  });

  const upload = useMutation({
    mutationFn: ({ file, url }: { file: File; url: string }) =>
      creativesApi.upload(campaignId, file, url),
    onSuccess: () => {
      toast.success('Creatividad subida');
      void queryClient.invalidateQueries({ queryKey: queryKeys.creative(campaignId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaign(campaignId) });
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const existing = creative.data;
  const missing = creative.isError && (creative.error as ApiRequestError).isNotFound;

  return (
    <Card title="Creatividad">
      {creative.isLoading ? (
        <Skeleton className="h-40" />
      ) : existing ? (
        <div className="flex flex-col gap-3">
          <div className="flex justify-center rounded border border-surface-tertiary bg-surface-primary p-4">
            {existing.type === 'VIDEO' ? (
              // Capped rather than shown at intrinsic size: a portrait rewarded video is
              // 720×1280 and would push the rest of the page off screen.
              <video
                src={existing.url}
                controls
                playsInline
                className="max-h-96 max-w-full"
              />
            ) : (
              <img
                src={existing.url}
                alt="Creatividad de la campaña"
                width={existing.width}
                height={existing.height}
                style={{ width: existing.width, height: existing.height }}
              />
            )}
          </div>
          <dl className="flex flex-col gap-2">
            <DetailRow label="Dimensiones" value={`${existing.width}×${existing.height}`} />
            <DetailRow label="Destino" value={existing.clickUrl} />
            <DetailRow label="Checksum" value={shortId(existing.checksum)} />
          </dl>
        </div>
      ) : missing ? (
        <EmptyState
          title="Sin creatividad"
          description={
            isRewarded
              ? 'Sube un video MP4 (máximo 20 MB) para poder lanzar la campaña.'
              : `Sube una imagen ${placement === 'BANNER' ? '320×50' : 'de al menos 300×250'} para poder lanzar la campaña.`
          }
        />
      ) : (
        <ErrorState message={(creative.error as Error).message} />
      )}

      <form
        className="mt-4 flex flex-col gap-3 border-t border-surface-tertiary pt-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const file = fileInput.current?.files?.[0];
          if (!file) {
            toast.error(isRewarded ? 'Selecciona un video' : 'Selecciona una imagen');
            return;
          }

          // Checked here as well as on the server. An oversized upload is
          // rejected by the servlet container before any of our code runs, so
          // the operator would wait for a long upload only to get a failure
          // that could have been caught the moment they picked the file.
          const limit = isRewarded ? 20 : 5;
          if (file.size > limit * 1024 * 1024) {
            const size = (file.size / 1024 / 1024).toFixed(1);
            toast.error(`El archivo pesa ${size} MB; el máximo es ${limit} MB.`);
            return;
          }

          // Safari offers .mov files even with accept="video/mp4". A QuickTime
          // file usually holds the same H.264 video, but Android often cannot
          // play the container — the ad would work on the operator's iPhone and
          // fail for half the audience.
          if (isRewarded && !/\.mp4$/i.test(file.name)) {
            toast.error(
              'El archivo debe ser MP4. Un .mov de iPhone no se reproduce en muchos Android.',
            );
            return;
          }

          // The server checks this too. Doing it here as well means the operator
          // finds out before the upload rather than after it: a video shorter
          // than the minimum leaves the user on a frozen frame, unable to close.
          if (isRewarded && minWatchSeconds) {
            const duration = await readVideoDuration(file);
            if (duration !== null && duration < minWatchSeconds) {
              toast.error(
                `El video dura ${Math.round(duration)} s y la campaña exige ${minWatchSeconds} s. ` +
                  'Usa un video más largo o baja el tiempo mínimo de la campaña.',
              );
              return;
            }
          }
          upload.mutate({ file, url: clickUrl });
        }}
      >
        <Field
          label={isRewarded ? 'Video' : 'Imagen'}
          htmlFor="creative-file"
          hint={
            isRewarded
              ? `Solo MP4, máximo 20 MB${minWatchSeconds ? `, mínimo ${minWatchSeconds} segundos` : ''}. Si grabaste con un iPhone tendrás un .mov: conviértelo antes, porque muchos Android no lo reproducen.`
              : undefined
          }
        >
          <input
            id="creative-file"
            ref={fileInput}
            type="file"
            accept={isRewarded ? 'video/mp4' : 'image/png,image/jpeg'}
            className="text-body-md text-text-secondary file:mr-3 file:rounded file:border-0 file:bg-surface-tertiary file:px-3 file:py-1.5 file:text-body-md file:text-text-primary"
          />
        </Field>

        <Field label="URL de destino" htmlFor="creative-url">
          <Input
            id="creative-url"
            type="url"
            required
            placeholder="https://anunciante.example/oferta"
            value={clickUrl}
            onChange={(event) => setClickUrl(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" loading={upload.isPending}>
          {existing ? 'Reemplazar creatividad' : 'Subir creatividad'}
        </Button>

        {existing && (
          <p className="text-label-md text-text-secondary">
            Reemplazar no afecta a las métricas acumuladas de la campaña.
          </p>
        )}
      </form>
    </Card>
  );
}

/**
 * The duration of a video file, read by the browser without uploading it.
 *
 * Returns null when the browser cannot decode it — the server checks the same
 * rule from the MP4 header, so a browser that cannot help is not a reason to
 * block the operator here.
 */
function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}
